import { useState, useEffect, SyntheticEvent, ChangeEvent } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { fetchLabOrders } from "shared/actions/labOrdersActions";
import { formatTimestampForDateTime } from "shared/utils/dates";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import { ControlledToggleButton } from "components/customInputs/buttons/ToggleButton";
import DateRange from "components/customInputs/DateRangeInput";
import TextInput from "components/customInputs/TextInput";
import {
  FilterObject,
  reduceFiltersForSearchParams,
  reduceFiltersToApi,
  resetFilters,
} from "components/filtersUtils";

import { FilterComponentProps } from "../types/adminTypes";

const OrdersFilters = ({
  sortBy,
  pageSize,
  pageIndex,
  setPage,
}: FilterComponentProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const usp = new URLSearchParams(location.search);
  const initializeFilters = (filters: FilterObject) => {
    return Object.keys(filters).reduce((acc: FilterObject, value) => {
      const initialFilter = usp.get(value);
      acc[value] = initialFilter ? initialFilter : "";
      return acc;
    }, {});
  };

  const [ordersFilters, setOrdersFilters] = useState<FilterObject>(
    initializeFilters({
      createdAtBefore: "",
      createdAtAfter: "",
      completedAtBefore: "",
      completedAtAfter: "",
      cancelledAtBefore: "",
      cancelledAtAfter: "",
      labId: "",
      caseAccession: "",
    })
  );

  const [isUnfulfilledQuery, setIsUnfulfilledQuery] = useState<boolean>(
    usp.get("isUnfulfilled") === "true"
  );

  useEffect(() => {
    filterOrders(ordersFilters, isUnfulfilledQuery);
  }, [sortBy, pageSize, pageIndex]);

  useEffect(() => {
    setSearchParams(ordersFilters, isUnfulfilledQuery);
  }, [sortBy, pageIndex]);

  const onChangeFilter = (key: string, value: string) => {
    setOrdersFilters({ ...ordersFilters, [key]: value });
  };

  const onChangeDateRangeFilter = (values: { [key: string]: Date }) => {
    setOrdersFilters(Object.assign({}, ordersFilters, values));
  };

  const setSearchParams = (filters: FilterObject, isUnfulfilled: boolean) => {
    const params = {
      ...reduceFiltersForSearchParams(filters),
      ...(isUnfulfilled && { isUnfulfilled: true }),
    } as any;
    if (pageIndex > 0) {
      params.page = pageIndex + 1;
    }
    if (sortBy.length) {
      params.sortBy = sortBy.map((s) => `${s.desc ? "-" : ""}${s.id}`);
    }
    navigate(`${location.pathname}?${new URLSearchParams(params).toString()}`, {
      replace: true,
    });
  };

  const filterOrders = (filters: FilterObject, isUnfulfilled: boolean) => {
    dispatch(
      fetchLabOrders({
        filters: {
          limit: pageSize,
          offset: pageSize * pageIndex,
          ordering: sortBy.length
            ? `${sortBy[0].desc ? "-" : ""}${sortBy[0].id}`
            : "-created_at",
          ...reduceFiltersToApi(filters),
          ...(isUnfulfilled && {
            isCompleted: false,
            isCancelled: false,
          }),
          isSent: true,
        },
      })
    );
  };

  const onSubmitFilter = (e: SyntheticEvent) => {
    e.preventDefault();
    setSearchParams(ordersFilters, isUnfulfilledQuery);
    filterOrders(ordersFilters, isUnfulfilledQuery);
    setPage && setPage(0);
  };

  const onResetFilter = () => {
    const newFilters = resetFilters(ordersFilters);
    setOrdersFilters(newFilters);
    setIsUnfulfilledQuery(false);
    setSearchParams(newFilters, false);
    filterOrders(newFilters, false);
    setPage && setPage(0);
  };

  return (
    <form className="AdminFilters" onSubmit={onSubmitFilter}>
      <div className="AdminFilters AdminFilters--vertical">
        <div className="AdminFilters__filters">
          <TextInput
            id="caseAccession"
            value={ordersFilters.caseAccession as string}
            label="Case Accession"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChangeFilter("caseAccession", e.target.value)
            }
          />
          <div className="AdminFilters--span">
            <DateRange
              id="createdAt"
              label="Created At"
              type="datetime-local"
              values={{
                from: {
                  id: "createdAtAfter",
                  name: "created_at_after",
                  value: ordersFilters.createdAtAfter
                    ? formatTimestampForDateTime(
                        ordersFilters.createdAtAfter as string
                      )
                    : "",
                },
                to: {
                  id: "createdAtBefore",
                  name: "created_at_before",
                  value: ordersFilters.createdAtBefore
                    ? formatTimestampForDateTime(
                        ordersFilters.createdAtBefore as string
                      )
                    : "",
                },
              }}
              onChangeDate={onChangeDateRangeFilter}
            />
          </div>
          <DateRange
            id="completedAt"
            label="Completed At"
            values={{
              from: {
                id: "completedAtAfter",
                name: "completed_at_after",
                value: ordersFilters.completedAtAfter as string,
              },
              to: {
                id: "completedAtBefore",
                name: "completed_at_before",
                value: ordersFilters.completedAtBefore as string,
              },
            }}
            onChangeDate={onChangeDateRangeFilter}
          />
          <DateRange
            id="cancelledAt"
            label="Cancelled At"
            values={{
              from: {
                id: "cancelledAtAfter",
                name: "cancelled_at_after",
                value: ordersFilters.cancelledAtAfter as string,
              },
              to: {
                id: "cancelledAtBefore",
                name: "cancelled_at_before",
                value: ordersFilters.cancelledAtBefore as string,
              },
            }}
            onChangeDate={onChangeDateRangeFilter}
          />
        </div>
      </div>
      <div className="AdminFilters__actions AdminFilters__actions--content">
        <ControlledToggleButton
          isToggled={isUnfulfilledQuery}
          onClick={() => {
            setIsUnfulfilledQuery(!isUnfulfilledQuery);
            setSearchParams(ordersFilters, !isUnfulfilledQuery);
            filterOrders(ordersFilters, !isUnfulfilledQuery);
          }}
        >
          Unfulfilled Orders
        </ControlledToggleButton>
        <div className="AdminFilters__actions--content">
          <PathologyWatchButton
            type="submit"
            color="green"
            title="filter cases"
          >
            Filter Orders
          </PathologyWatchButton>
          <PathologyWatchButton onClick={onResetFilter} title="reset filters">
            Reset Filters
          </PathologyWatchButton>
        </div>
      </div>
    </form>
  );
};

export default OrdersFilters;
